<template>
  <!-- 送信中オーバーレイ -->
  <div class="overlay" v-if="isSending">
    <div class="sending_box">
      <div class="spinner"></div>
      <p>送信中...</p>
    </div>
  </div>

  <!-- 送信完了画面 -->
  <div class="complete_wrapper" v-else-if="isSended">
    <div class="complete_card">
      <div class="complete_icon">✓</div>
      <h2>お申し込みが完了しました</h2>
      <p>ご入力いただいたメールアドレスに確認メールをお送りしました。<br>担当者より追ってご連絡いたします。</p>
      <button class="btn_primary" @click="handleCloseForm">トップへ戻る</button>
    </div>
  </div>

  <!-- 個人情報保護方針モーダル（フォームの上に重ねて表示） -->
  <PersonalRuleModal v-if="isShowPIP" @close-pip="handleShowPIP" />

  <!-- 【緊急停止】応募受付停止中の表示 -->
  <div class="complete_wrapper" v-else-if="formClosed">
    <div class="complete_card">
      <h2>ただいま応募受付を一時停止しております</h2>
      <p>ご不便をおかけし申し訳ございません。<br>お問い合わせは m-inoue@mik-s.jp までご連絡ください。</p>
      <button class="btn_primary" @click="handleCloseForm">トップへ戻る</button>
    </div>
  </div>

  <!-- 応募フォーム本体 -->
  <form class="form_card" @submit.prevent="sendEmail" v-else>

    <!-- ヘッダー -->
    <div class="form_header">
      <button type="button" class="close_btn" @click="handleCloseForm" aria-label="閉じる">✕</button>
      <img src="@/assets/mik_logo.svg" alt="MIK" class="form_logo">
      <h1 class="form_title">カジュアル面談 お申し込みフォーム</h1>
      <p class="form_subtitle">お気軽にご応募ください。担当者よりご連絡いたします。</p>
    </div>

    <!-- フォーム本体 -->
    <div class="form_body">

      <!-- 基本情報 -->
      <div class="section_label">基本情報</div>

      <div class="field_row">
        <div class="field">
          <label class="field_label">氏名 <span class="badge_required">必須</span></label>
          <input type="text" v-model="name" ref="nameField" placeholder="例：田中 太郎"
            class="field_input" required @keyup.enter="focusKanaField">
        </div>
        <div class="field">
          <label class="field_label">氏名（カナ）<span class="badge_required">必須</span></label>
          <input type="text" v-model="kana" ref="kanaField" placeholder="例：タナカ タロウ"
            class="field_input" required pattern="[ァ-ヴー・\s\u3000]+" title="全角カタカナで入力してください" @keyup.enter="focusBirthYearField">
        </div>
      </div>

      <div class="field_row">
        <div class="field">
          <label class="field_label">生まれ年 <span class="badge_required">必須</span></label>
          <div class="select_wrapper">
            <select v-model="birth_year" ref="birthYearField" class="field_select" required
              @keyup.enter="focusEmailField">
              <option value="" disabled>選択してください</option>
              <option :value="year" v-for="year in years" :key="year">{{ year }}年</option>
            </select>
          </div>
        </div>
        <div class="field">
          <label class="field_label">連絡先電話番号</label>
          <input type="tel" v-model="phone" ref="phoneField" placeholder="例：090-0000-0000"
            class="field_input" @keyup.enter="focusEmailField">
        </div>
      </div>

      <div class="field">
        <label class="field_label">メールアドレス <span class="badge_required">必須</span></label>
        <input type="email" v-model="mail" ref="emailField" placeholder="例：tanaka@example.com"
          class="field_input" required @keyup.enter="focusJobField">
      </div>

      <!-- 希望条件 -->
      <div class="section_label" style="margin-top: 28px;">希望条件</div>

      <div class="field_row">
        <div class="field">
          <label class="field_label">希望職種</label>
          <div class="select_wrapper">
            <select v-model="job_type" ref="jobField" class="field_select" @keydown.enter="focusAreaFieldField">
              <option value="">指定なし</option>
              <option v-for="type in jobs" :key="type" :value="type">{{ type }}</option>
            </select>
          </div>
        </div>
        <div class="field">
          <label class="field_label">希望勤務地</label>
          <div class="select_wrapper">
            <select v-model="job_area" ref="areaField" class="field_select" @keydown.enter="focusMendan1Field">
              <option value="">指定なし</option>
              <option :value="area" v-for="area in areas" :key="area">{{ area }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- 面談希望日 -->
      <div class="section_label" style="margin-top: 28px;">面談希望日</div>
      <p class="section_note">ご希望の日程を最大3つまでお選びください</p>

      <div class="field_row three_col">
        <div class="field">
          <label class="field_label">第1希望</label>
          <input type="date" v-model="mendan1" ref="mendan1Field" class="field_input"
            @keydown.enter="focusMendan2Field">
        </div>
        <div class="field">
          <label class="field_label">第2希望</label>
          <input type="date" v-model="mendan2" ref="mendan2Field" class="field_input"
            @keydown.enter="focusMendan3Field">
        </div>
        <div class="field">
          <label class="field_label">第3希望</label>
          <input type="date" v-model="mendan3" ref="mendan3Field" class="field_input"
            @keydown.enter="focusYoubouField">
        </div>
      </div>

      <!-- その他 -->
      <div class="section_label" style="margin-top: 28px;">その他</div>

      <div class="field">
        <label class="field_label">ご要望・メッセージ</label>
        <textarea v-model="youbou" ref="youbouField" class="field_textarea"
          placeholder="ご質問や気になることがあればお気軽にどうぞ"></textarea>
      </div>

      <!-- ボット対策（非表示の入力欄。人間は入力しない） -->
      <div class="hp" aria-hidden="true">
        <label>website<input type="text" v-model="website" tabindex="-1" autocomplete="off"></label>
      </div>

      <!-- 同意・送信 -->
      <div class="consent_area">
        <label class="consent_label">
          <input type="checkbox" v-model="isChecked" class="consent_check">
          <span>「<button type="button" class="pip_link" @click="handleShowPIP">個人情報保護方針</button>」に同意する</span>
        </label>
      </div>

      <TurnstileWidget ref="turnstile" @verified="t => turnstileToken = t" @expired="turnstileToken = ''" />

      <button type="submit" class="btn_submit" :disabled="!isChecked || isSending || (turnstileRequired && !turnstileToken)">
        {{ isSending ? '送信中...' : '申し込む' }}
      </button>

    </div>
  </form>
</template>

<script>
import { ref } from 'vue';
import PersonalRuleModal from './PIP.vue';
import TurnstileWidget from './TurnstileWidget.vue';

export default {
  components: { PersonalRuleModal, TurnstileWidget },
  emits: ['close_form'],
  setup(props, { emit }) {
    const name = ref("");
    const kana = ref("");
    const birth_year = ref("");
    const phone = ref("");
    const mail = ref("");
    const check_mail = ref("");
    const job_type = ref("");
    const job_area = ref("");
    const mendan1 = ref("");
    const mendan2 = ref("");
    const mendan3 = ref("");
    const youbou = ref("");

    const nameField = ref(null);
    const kanaField = ref(null);
    const birthYearField = ref(null);
    const phoneField = ref(null);
    const emailField = ref(null);
    const jobField = ref(null);
    const areaField = ref(null);
    const mendan1Field = ref(null);
    const mendan2Field = ref(null);
    const mendan3Field = ref(null);
    const youbouField = ref(null);

    const focusKanaField = () => { if (name.value) kanaField.value?.focus(); };
    const focusBirthYearField = () => { if (kana.value) birthYearField.value?.focus(); };
    const focusEmailField = () => { if (birth_year.value) emailField.value?.focus(); };
    const focusPhoneField = () => { phoneField.value?.focus(); };
    const focusJobField = () => { jobField.value?.focus(); };
    const focusAreaFieldField = () => { areaField.value?.focus(); };
    const focusMendan1Field = () => { mendan1Field.value?.focus(); };
    const focusMendan2Field = () => { mendan2Field.value?.focus(); };
    const focusMendan3Field = () => { mendan3Field.value?.focus(); };
    const focusYoubouField = () => { youbouField.value?.focus(); };

    const years = ref([]);
    for (let y = 2007; y >= 1965; y--) years.value.push(y);

    const areas = ref(["札幌", "盛岡", "東京", "大阪", "福岡"]); // TODO: テスト用・後で削除
    const jobs = ref(["ソフトウェア開発", "インフラ", "移動体通信", "建築・土木"]);

    // 受付停止スイッチ：VUE_APP_APPLICATION_FORM_OPEN=true でビルドしたときだけフォームを表示
    const formClosed = process.env.VUE_APP_APPLICATION_FORM_OPEN !== 'true';

    // ボット対策
    const website = ref("");                 // ハニーポット
    const openedAt = Date.now();             // フォーム表示時刻（送信までの時間を計測）
    const turnstile = ref(null);
    const turnstileToken = ref("");
    const turnstileRequired = !!process.env.VUE_APP_TURNSTILE_SITE_KEY;
    const isShowPIP = ref(false);
    const isChecked = ref(false);
    const isSending = ref(false);
    const isSended = ref(false);

    const handleShowPIP = () => { isShowPIP.value = !isShowPIP.value; };
    const handleCloseForm = () => { isSended.value = false; emit("close_form"); };

    const sendEmail = async () => {
      if (isSending.value) return;
      isSending.value = true;
      try {
        const response = await fetch('/api/applications', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.value, kana: kana.value, birth_year: birth_year.value,
            phone: phone.value, mail: mail.value, job_type: job_type.value,
            job_area: job_area.value, mendan1: mendan1.value,
            mendan2: mendan2.value, mendan3: mendan3.value, youbou: youbou.value,
            website: website.value, elapsed: Date.now() - openedAt,
            turnstileToken: turnstileToken.value,
          }),
        });

        if (response.ok) {
          isSended.value = true;
          name.value = ""; kana.value = ""; birth_year.value = ""; phone.value = "";
          mail.value = ""; job_type.value = ""; job_area.value = "";
          mendan1.value = ""; mendan2.value = ""; mendan3.value = ""; youbou.value = "";
        } else {
          const err = await response.json().catch(() => ({}));
          alert('送信に失敗しました: ' + (err.message || ''));
          turnstile.value?.reset();
        }
      } catch (error) {
        console.error('Error:', error);
        alert('送信中にエラーが発生しました。');
      } finally {
        isSending.value = false;
      }
    };

    return {
      formClosed, website, turnstile, turnstileToken, turnstileRequired,
      name, kana, birth_year, phone, mail, check_mail, job_type, job_area,
      mendan1, mendan2, mendan3, youbou, years, areas, jobs,
      nameField, kanaField, birthYearField, phoneField, emailField,
      jobField, areaField, mendan1Field, mendan2Field, mendan3Field, youbouField,
      focusKanaField, focusBirthYearField, focusEmailField, focusPhoneField,
      focusJobField, focusAreaFieldField, focusMendan1Field, focusMendan2Field,
      focusMendan3Field, focusYoubouField,
      isShowPIP, handleShowPIP, isChecked, isSending, isSended,
      sendEmail, handleCloseForm,
    };
  }
};
</script>

<style scoped>
/* ===== カード ===== */
.form_card {
  width: 100%;
  max-width: 680px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  margin: 0 auto;
}

/* ===== ヘッダー ===== */
.form_header {
  background: linear-gradient(135deg, #0cc0df 0%, #0890a8 100%);
  padding: 32px 36px 28px;
  text-align: center;
  position: relative;
}
.close_btn {
  position: absolute;
  top: 16px;
  right: 18px;
  background: rgba(255,255,255,0.2);
  border: none;
  color: #fff;
  font-size: 18px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  cursor: pointer;
  transition: 200ms;
  display: flex;
  align-items: center;
  justify-content: center;
}
.close_btn:hover { background: rgba(255,255,255,0.35); }
.form_logo { width: 130px; margin-bottom: 14px; filter: brightness(0) invert(1); }
.form_title {
  color: #fff;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 8px;
}
.form_subtitle { color: rgba(255,255,255,0.85); font-size: 13px; margin: 0; }

/* ===== ボディ ===== */
.form_body { padding: 32px 36px 36px; }

.section_label {
  font-size: 13px;
  font-weight: bold;
  color: #0cc0df;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-bottom: 2px solid #e8f9fc;
  padding-bottom: 6px;
  margin-bottom: 18px;
}
.section_note { font-size: 12px; color: #888; margin: -12px 0 16px; }

/* ===== フィールド ===== */
.field_row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}
.field_row.three_col { grid-template-columns: 1fr 1fr 1fr; }
.field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; }
.field_row .field { margin-bottom: 0; }

.field_label {
  font-size: 13px;
  font-weight: 600;
  color: #444;
  display: flex;
  align-items: center;
  gap: 6px;
}
.badge_required {
  background: #ff6b6b;
  color: #fff;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 3px;
}

.field_input, .field_select, .field_textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1.5px solid #dde1e7;
  border-radius: 8px;
  font-size: 15px;
  color: #333;
  background: #fafbfc;
  transition: border-color 200ms, box-shadow 200ms;
  outline: none;
  font-family: inherit;
}
.field_input:focus, .field_select:focus, .field_textarea:focus {
  border-color: #0cc0df;
  box-shadow: 0 0 0 3px rgba(12, 192, 223, 0.12);
  background: #fff;
}
.field_input::placeholder, .field_textarea::placeholder { color: #bbb; }
.field_textarea { height: 110px; resize: vertical; }

.select_wrapper { position: relative; }
.select_wrapper::after {
  content: '▾';
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  pointer-events: none;
  font-size: 14px;
}
.field_select { appearance: none; -webkit-appearance: none; padding-right: 36px; }

/* ===== 同意 ===== */
.consent_area {
  background: #f7f9fc;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 24px;
}
.consent_label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #555;
  cursor: pointer;
}
.consent_check { width: 18px; height: 18px; cursor: pointer; accent-color: #0cc0df; flex-shrink: 0; }
.pip_link {
  background: none;
  border: none;
  color: #0cc0df;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  font-size: 14px;
  text-decoration: underline;
}

/* ===== ボタン ===== */
.btn_submit {
  width: 100%;
  height: 52px;
  background: linear-gradient(135deg, #0cc0df, #0890a8);
  color: #fff;
  font-size: 17px;
  font-weight: bold;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: 200ms;
  letter-spacing: 0.05em;
}
.btn_submit:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(12,192,223,0.35); }
.btn_submit:disabled { background: #c8d0d8; cursor: not-allowed; }

.btn_primary {
  padding: 12px 36px;
  background: linear-gradient(135deg, #0cc0df, #0890a8);
  color: #fff;
  font-size: 15px;
  font-weight: bold;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  margin-top: 20px;
  transition: 200ms;
}
.btn_primary:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(12,192,223,0.35); }

/* ===== 送信中オーバーレイ ===== */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(255,255,255,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.sending_box {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #555;
  font-size: 16px;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e0f7fb;
  border-top-color: #0cc0df;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== 完了画面 ===== */
.complete_wrapper {
  position: fixed;
  inset: 0;
  background: #f4f8fb;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.complete_card {
  background: #fff;
  border-radius: 20px;
  padding: 52px 48px;
  text-align: center;
  box-shadow: 0 8px 40px rgba(0,0,0,0.1);
  max-width: 440px;
}
.complete_icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #0cc0df, #0890a8);
  color: #fff;
  font-size: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}
.complete_card h2 { font-size: 20px; color: #2c3e50; margin: 0 0 12px; }
.complete_card p { font-size: 14px; color: #666; line-height: 1.7; margin: 0; }

/* ===== PIP オーバーレイ ===== */
.pip_overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

/* ===== レスポンシブ ===== */
@media (max-width: 600px) {
  .form_card { border-radius: 0; box-shadow: none; }
  .form_header { padding: 24px 20px 20px; }
  .form_body { padding: 24px 20px 28px; }
  .field_row { grid-template-columns: 1fr; }
  .field_row.three_col { grid-template-columns: 1fr; }
  .form_title { font-size: 17px; }
  .form_logo { width: 100px; }
}
.hp { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
</style>
